import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Calendar, TrendingUp, Eye, Upload, Loader2, Trash2, Share2 } from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

export const ReportsListPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    loadReports();
  }, []);

  const handleDeleteReport = async (shareToken, clientName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le rapport de ${clientName} ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/reports/${shareToken}`, {
        headers: getAuthHeader()
      });
      
      // Recharger la liste après suppression
      loadReports();
    } catch (err) {
      console.error('Error deleting report:', err);
      setError('Erreur lors de la suppression du rapport');
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reports`, {
        headers: getAuthHeader()
      });
      setReports(response.data.reports || []);
      setError(null);
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/20 to-background">
        <Navigation />
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Chargement des rapports...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-accent/20 to-background">
      <Navigation />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12 animate-fade-in-up">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold font-display mb-2">
                Mes <span className="gradient-text">Rapports TICFE</span>
              </h1>
              <p className="text-lg text-muted-foreground">
                {reports.length} {reports.length === 1 ? 'rapport transformé' : 'rapports transformés'}
              </p>
            </div>
            
            <Link to="/upload">
              <Button size="lg" className="shadow-lg">
                <Upload className="w-5 h-5 mr-2" />
                Nouveau rapport
              </Button>
            </Link>
          </div>

          {error && (
            <Card className="border-destructive/50 bg-destructive/5 mb-6">
              <CardContent className="p-6">
                <p className="text-destructive">{error}</p>
                <Button onClick={loadReports} className="mt-4">Réessayer</Button>
              </CardContent>
            </Card>
          )}

          {!error && reports.length === 0 ? (
            <Card className="border-2 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="w-20 h-20 bg-accent rounded-full flex items-center justify-center mb-6">
                  <FileText className="w-10 h-10 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Aucun rapport disponible</h3>
                <p className="text-muted-foreground mb-6 text-center max-w-md">
                  Vous n'avez pas encore chargé de rapport. Commencez par charger votre premier rapport TICFE.
                </p>
                <Link to="/upload">
                  <Button>
                    <Upload className="w-4 h-4 mr-2" />
                    Charger un rapport
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {reports.map((report) => (
                <ReportCard 
                  key={report._id} 
                  report={report} 
                  onDelete={handleDeleteReport}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ReportCard = ({ report, onDelete }) => {
  const uploadDate = new Date(report.created_at || report.upload_date || Date.now());
  
  const handleViewReport = () => {
    // Marquer qu'on vient de la liste des rapports (mode admin)
    sessionStorage.setItem('from_reports_list', 'true');
  };

  const handleShare = () => {
    const shareLink = `${window.location.origin}/report/${report.share_token}`;
    navigator.clipboard.writeText(shareLink);
    toast.success('Lien copié !', {
      description: 'Le lien du rapport a été copié dans le presse-papiers',
    });
  };
  
  return (
    <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div className="flex-1">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl font-display mb-1">{report.client_name}</CardTitle>
                <CardDescription className="flex flex-col gap-1">
                  <span className="flex items-center gap-2">
                    <Calendar className="w-3 h-3" />
                    {format(uploadDate, "d MMMM yyyy 'à' HH:mm", { locale: fr })}
                  </span>
                  <span className="text-xs">PDL: {report.pdl}</span>
                </CardDescription>
              </div>
            </div>
          </div>
          
          <Badge 
            variant="outline" 
            className={
              report.eligibility === 'Non-Eligible' 
                ? "bg-destructive/10 text-destructive border-destructive/20" 
                : "bg-success/10 text-success border-success/20"
            }
          >
            {report.eligibility || 'Eligible'}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-accent/50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Attestation (12 mois)</span>
            </div>
            <p className="text-2xl font-bold text-primary">
              {report.savings?.attestation ? report.savings.attestation.toLocaleString('fr-FR') : '0'} €
            </p>
          </div>
          
          <div className="bg-accent/50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Remboursement 2024</span>
            </div>
            <p className="text-2xl font-bold text-primary">
              {report.savings?.reimbursement?.year_2024 ? report.savings.reimbursement.year_2024.toLocaleString('fr-FR') : '0'} €
            </p>
          </div>
          
          <div className="bg-accent/50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Remboursement 2025</span>
            </div>
            <p className="text-2xl font-bold text-primary">
              {report.savings?.reimbursement?.year_2025 ? report.savings.reimbursement.year_2025.toLocaleString('fr-FR') : '0'} €
            </p>
          </div>
        </div>
        
        <div className="flex gap-3 flex-col sm:flex-row">
          <Link to={`/report/${report.share_token}`} onClick={handleViewReport} className="flex-1">
            <Button className="w-full" size="lg">
              <Eye className="w-4 h-4 mr-2" />
              Voir le rapport
            </Button>
          </Link>
          <Button 
            variant="outline" 
            size="lg"
            onClick={handleShare}
          >
            <Share2 className="w-4 h-4 mr-2" />
            Partager
          </Button>
          <Button 
            variant="destructive" 
            size="lg"
            onClick={() => onDelete(report.share_token, report.client_name)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReportsListPage;
